import React, { useState, useEffect } from "react";
import { apiFetch } from "../../api/apiClient";

import {
  Box,
  Typography,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import Editor from "@monaco-editor/react";
import SaveIcon from "@mui/icons-material/Save";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SidebarLayout from "../../shared/sections/sidebar/SidebarLayout";
import ViewCompactIcon from "@mui/icons-material/ViewCompact";
import CodeIcon from "@mui/icons-material/Code";
import TableChartIcon from "@mui/icons-material/TableChart";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditIcon from "@mui/icons-material/Edit";
import ImageSchemaEditor from "./ImageSchemaEditor";
import ImageSchemaManagementV2 from "./ImageSchemaManagementV2";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PerlButton from "../../shared/components/PerlButton";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DataObjectIcon from "@mui/icons-material/Dataset";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import ReactJson from "@microlink/react-json-view";
import { perlColors } from "../../design/tokens";
import { useAuth } from "../../context/AuthContext";

const API_BASE = "/api/admin";

const SchemaManagementPage = () => {
  const { user } = useAuth();
  // Navigation state
  const [activeSection, setActiveSection] = useState("data"); // "data" or "image"
  const [sectionLoading, setSectionLoading] = useState(false);

  // State management
  const [towers, setTowers] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [selectedTower, setSelectedTower] = useState("");
  const [selectedCommodity, setSelectedCommodity] = useState("");
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [editorContent, setEditorContent] = useState("");
  const [versions, setVersions] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // New state for tabbed editing (Prompts, Schema, Config)
  const [editorTab, setEditorTab] = useState(0); // 0=System Prompt, 1=User Prompt, 2=Schema Builder, 3=Model Config
  const [systemPrompt, setSystemPrompt] = useState("");
  const [userPromptTemplate, setUserPromptTemplate] = useState("");
  const [outputSchema, setOutputSchema] = useState("");
  const [schemaFields, setSchemaFields] = useState([]); // Parsed fields for visual editor
  const [modelConfig, setModelConfig] = useState({
    model: "gpt-4o",
    temperature: 0.7,
    max_tokens: 4000,
  });
  const [schemaValidation, setSchemaValidation] = useState({
    isValid: true,
    errors: [],
    warnings: [],
  });
  const [schemaViewMode, setSchemaViewMode] = useState("builder"); // "builder", "visual" or "json"
  const [editingField, setEditingField] = useState(null);
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [expandedFields, setExpandedFields] = useState({});
  const [movingField, setMovingField] = useState(null); // Track which field is being moved
  const [selectedField, setSelectedField] = useState(null); // Track which field is selected
  const [isCreatingNew, setIsCreatingNew] = useState(false); // Track if creating new schema
  const [newSchemaVersion, setNewSchemaVersion] = useState("");
  const [newField, setNewField] = useState({
    name: "",
    type: "string",
    description: "",
    required: false,
    enum: [],
    minimum: undefined,
    maximum: undefined,
    minLength: undefined,
    maxLength: undefined,
    pattern: "",
    format: "",
  });

  // Initialize new schema creation
  const handleCreateNewSchema = () => {
    // Show version input dialog first
    setNewSchemaVersion("v1.0");
    setVersionDialogOpen(true);
  };

  // Confirm new schema creation with version
  const confirmCreateNewSchema = () => {
    if (!newSchemaVersion.trim()) {
      setError("Version is required");
      return;
    }

    setVersionDialogOpen(false);
    setIsCreatingNew(true);

    // Initialize with blank schema structure
    const blankSchema = {
      type: "object",
      properties: {},
      required: [],
    };

    const systemPromptText =
      "You are an AI assistant specialized in cost estimation and analysis for the " +
      selectedCommodity +
      " domain.";
    const userPromptText =
      "Based on the provided input data, generate a detailed cost analysis including formulation, recipe details, and cost breakdown.";

    setSystemPrompt(systemPromptText);
    setUserPromptTemplate(userPromptText);
    setOutputSchema(JSON.stringify(blankSchema, null, 2));
    setModelConfig({
      model: "gpt-4o",
      temperature: 0.7,
      max_tokens: 4000,
    });

    // Create full content JSON for editor
    const fullContentJson = {
      schema_version: "1.0",
      operations: {
        unified_quick_cost: {
          system_prompt: systemPromptText,
          user_prompt_template: userPromptText,
          output_schema: blankSchema,
          model: "gpt-4o",
          temperature: 0.7,
          max_tokens: 4000,
        },
      },
    };

    // Set editor content
    setEditorContent(JSON.stringify(fullContentJson, null, 2));
    setTreeData(fullContentJson);
    setTableData(flattenJSON(fullContentJson));

    // Create a temporary schema object for editing
    setSelectedSchema({
      id: null,
      tower_code: selectedTower,
      commodity_code: selectedCommodity,
      version: newSchemaVersion,
      is_active: false,
      content_json: fullContentJson,
    });

    setHasUnsavedChanges(true);
  };

  // Handle move up/down for nested fields
  const handleMoveFieldNested = (parentPath, key, direction, depth) => {
    try {
      const fullPath = parentPath ? `${parentPath}.${key}` : key;
      setMovingField(fullPath);

      const schema = JSON.parse(outputSchema);

      // Navigate to the parent object
      let targetObj = schema.properties;
      if (parentPath) {
        const pathParts = parentPath.split(".");
        for (const part of pathParts) {
          if (targetObj[part]?.type === "object") {
            targetObj = targetObj[part].properties;
          } else if (targetObj[part]?.type === "array" && targetObj[part].items?.properties) {
            targetObj = targetObj[part].items.properties;
          }
        }
      }

      const propertyKeys = Object.keys(targetObj);
      const currentIndex = propertyKeys.indexOf(key);

      if (direction === "up" && currentIndex > 0) {
        const newIndex = currentIndex - 1;
        [propertyKeys[currentIndex], propertyKeys[newIndex]] = [
          propertyKeys[newIndex],
          propertyKeys[currentIndex],
        ];
      } else if (direction === "down" && currentIndex < propertyKeys.length - 1) {
        const newIndex = currentIndex + 1;
        [propertyKeys[currentIndex], propertyKeys[newIndex]] = [
          propertyKeys[newIndex],
          propertyKeys[currentIndex],
        ];
      } else {
        setMovingField(null);
        return;
      }

      // Rebuild properties object in new order
      const newProperties = {};
      propertyKeys.forEach((k) => {
        newProperties[k] = targetObj[k];
      });

      // Update the target object with reordered properties
      if (parentPath) {
        const pathParts = parentPath.split(".");
        let current = schema.properties;
        for (let i = 0; i < pathParts.length; i++) {
          const part = pathParts[i];
          if (i === pathParts.length - 1) {
            if (current[part]?.type === "object") {
              current[part].properties = newProperties;
            }
          } else {
            if (current[part]?.type === "object") {
              current = current[part].properties;
            } else if (current[part]?.type === "array" && current[part].items?.properties) {
              current = current[part].items.properties;
            }
          }
        }
      } else {
        schema.properties = newProperties;
      }

      setOutputSchema(JSON.stringify(schema, null, 2));
      setHasUnsavedChanges(true);

      setTimeout(() => setMovingField(null), 600);
    } catch (e) {
      setError(`Failed to reorder nested field: ${e.message}`);
      setMovingField(null);
    }
  };

  // Handle move up/down
  const handleMoveField = (key, direction) => {
    try {
      // Highlight the moving field
      setMovingField(key);

      const schema = JSON.parse(outputSchema);
      const properties = schema.properties || {};
      const propertyKeys = Object.keys(properties);
      const currentIndex = propertyKeys.indexOf(key);

      if (direction === "up" && currentIndex > 0) {
        const newIndex = currentIndex - 1;
        [propertyKeys[currentIndex], propertyKeys[newIndex]] = [
          propertyKeys[newIndex],
          propertyKeys[currentIndex],
        ];
      } else if (direction === "down" && currentIndex < propertyKeys.length - 1) {
        const newIndex = currentIndex + 1;
        [propertyKeys[currentIndex], propertyKeys[newIndex]] = [
          propertyKeys[newIndex],
          propertyKeys[currentIndex],
        ];
      } else {
        setMovingField(null);
        return; // Can't move
      }

      // Rebuild properties object in new order
      const newProperties = {};
      propertyKeys.forEach((k) => {
        newProperties[k] = properties[k];
      });

      schema.properties = newProperties;
      setOutputSchema(JSON.stringify(schema, null, 2));
      setHasUnsavedChanges(true);

      // Clear highlight after animation
      setTimeout(() => setMovingField(null), 600);
    } catch (e) {
      setError(`Failed to reorder field: ${e.message}`);
      setMovingField(null);
    }
  };

  // Dialog states
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newVersion, setNewVersion] = useState("");
  const [versionDescription, setVersionDescription] = useState("");
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [versionToActivate, setVersionToActivate] = useState(null);
  const [viewMode, setViewMode] = useState("code"); // "code", "tree", or "table"
  const [editingNode, setEditingNode] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [treeData, setTreeData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Image schema state
  const [imageSchemas, setImageSchemas] = useState([]);
  const [selectedImageSchema, setSelectedImageSchema] = useState(null);
  const [imageSchemaDialogOpen, setImageSchemaDialogOpen] = useState(false);

  // Prompt editor dialog
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [promptType, setPromptType] = useState(""); // "system" or "user"
  const [promptPath, setPromptPath] = useState(""); // JSON path to the prompt
  const [promptValue, setPromptValue] = useState("");
  const [selectedItem, setSelectedItem] = useState(null); // For editing panel
  const [focusMode, setFocusMode] = useState(false); // Focus mode toggle
  const [versionDialogOpen, setVersionDialogOpen] = useState(false); // Version dialog for new schema

  const normalizedRole = (() => {
    if (Array.isArray(user?.role) && user.role.length > 0) {
      return String(user.role[0]?.role_name || "").trim().toLowerCase();
    }
    return String(user?.role || "").trim().toLowerCase();
  })();

  const isSuperAdmin = normalizedRole === "super admin";

  // Load towers on mount
  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchTowers();
  }, [isSuperAdmin]);

  // Load commodities when tower changes
  useEffect(() => {
    if (!isSuperAdmin) return;
    if (selectedTower) {
      fetchCommodities(selectedTower);
    }
  }, [selectedTower, isSuperAdmin]);

  // Load schemas when commodity changes
  useEffect(() => {
    if (!isSuperAdmin) return;
    if (selectedTower && selectedCommodity) {
      fetchSchemas();
    }
  }, [selectedTower, selectedCommodity, isSuperAdmin]);

  // Load image schemas when tower/commodity changes
  useEffect(() => {
    if (!isSuperAdmin) return;
    if (selectedTower && selectedCommodity) {
      fetchImageSchemas();
    }
  }, [selectedTower, selectedCommodity, isSuperAdmin]);

  // API calls
  const fetchTowers = async () => {
    try {
      const response = await apiFetch(`${API_BASE}/towers`);
      const data = await response.json();
      setTowers(data.towers || []);
    } catch (error) {
      setError("Failed to load towers");
      console.error(error);
    }
  };

  const fetchCommodities = async (tower) => {
    try {
      const response = await apiFetch(`${API_BASE}/commodities?tower=${tower}`);
      const data = await response.json();
      setCommodities(data.commodities || []);
    } catch (error) {
      setError("Failed to load commodities");
      console.error(error);
    }
  };

  const fetchSchemas = async () => {
    try {
      setLoading(true);
      const response = await apiFetch(
        `${API_BASE}/schemas?tower=${selectedTower}&commodity=${selectedCommodity}`
      );
      const data = await response.json();
      setSchemas(data.schemas || []);

      // Auto-select active schema
      const activeSchema = (data.schemas || []).find((s) => s.is_active);
      if (activeSchema) {
        loadSchema(activeSchema.id);
      } else if ((data.schemas || []).length === 0) {
        // No schemas found, don't auto-create but reset creation state
        setIsCreatingNew(false);
      }
    } catch (error) {
      setError("Failed to load schemas");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadSchema = async (schemaId, isViewingVersion = false) => {
    try {
      setLoading(true);
      const response = await apiFetch(`${API_BASE}/schemas/${schemaId}`);
      const data = await response.json();

      if (data.schema) {
        setSelectedSchema(data.schema);
        const contentJson = data.schema.content_json;

        // Parse and set individual sections
        const operations = contentJson.operations?.unified_quick_cost || {};
        setSystemPrompt(operations.system_prompt || "");
        setUserPromptTemplate(operations.user_prompt_template || "");
        setOutputSchema(JSON.stringify(operations.output_schema || {}, null, 2));
        setModelConfig({
          model: operations.model || "gpt-4o",
          temperature: operations.temperature || 0.7,
          max_tokens: operations.max_tokens || 4000,
        });

        // Keep old states for backward compatibility
        const jsonContent = JSON.stringify(contentJson, null, 2);
        setEditorContent(jsonContent);
        setTreeData(contentJson);
        setTableData(flattenJSON(contentJson));

        // Only reset unsaved changes flag if not viewing a version
        if (!isViewingVersion) {
          setHasUnsavedChanges(false);
        }

        loadHistory(schemaId);

        // Show info message if viewing a specific version
        if (isViewingVersion) {
          setSuccess(`Viewing version ${data.schema.version} (read-only preview)`);
        }
      }
    } catch (error) {
      setError("Failed to load schema");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (schemaId) => {
    try {
      const response = await apiFetch(`${API_BASE}/schemas/${schemaId}/history`);
      const data = await response.json();
      setVersions(data.versions || []);
      setAuditLog(data.audit_log || []);
    } catch (error) {
      console.error("Failed to load history:", error);
    }
  };

  const handleSaveSchema = async () => {
    try {
      // Validate JSON
      const parsedJson = JSON.parse(editorContent);

      setSaving(true);
      const response = await apiFetch(`${API_BASE}/schemas/${selectedSchema.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          version: newVersion,
          content_json: parsedJson,
          description: versionDescription,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Version ${newVersion} created successfully!`);
        setSaveDialogOpen(false);
        setNewVersion("");
        setVersionDescription("");
        fetchSchemas();
      } else {
        setError(data.error || "Failed to save schema");
      }
    } catch (error) {
      setError("Invalid JSON or save failed: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleActivateVersion = async () => {
    try {
      setSaving(true);
      const response = await apiFetch(`${API_BASE}/schemas/${versionToActivate}/activate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          notes: "Activated from Schema Management UI",
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess("Version activated successfully!");
        setActivateDialogOpen(false);
        setVersionToActivate(null);
        fetchSchemas();
      } else {
        setError(data.error || "Failed to activate version");
      }
    } catch (error) {
      setError("Failed to activate version");
    } finally {
      setSaving(false);
    }
  };

  const handleEditorChange = (value) => {
    console.log("🔧 Editor changed, setting hasUnsavedChanges to true");
    setEditorContent(value || "");
    setHasUnsavedChanges(true);
    // Update tree data when code changes
    try {
      const parsed = JSON.parse(value || "{}");
      setTreeData(parsed);
      setTableData(flattenJSON(parsed));
    } catch (e) {
      // Invalid JSON, keep old tree data
    }
  };

  // Open prompt editor dialog
  // Open prompt editor dialog
  const openPromptEditor = (type, path) => {
    try {
      const parsed = JSON.parse(editorContent);
      const keys = path.split(".");
      let value = parsed;
      for (const key of keys) {
        value = value[key];
      }
      setPromptType(type);
      setPromptPath(path);
      setPromptValue(value || "");
      setPromptDialogOpen(true);
    } catch (e) {
      setError("Failed to parse JSON: " + e.message);
    }
  };

  // Save prompt changes
  const savePromptChanges = () => {
    try {
      const parsed = JSON.parse(editorContent);
      const keys = promptPath.split(".");
      let current = parsed;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = promptValue;
      setEditorContent(JSON.stringify(parsed, null, 2));
      setHasUnsavedChanges(true);
      setPromptDialogOpen(false);
      setSuccess("Prompt updated successfully!");
    } catch (e) {
      setError("Failed to update prompt: " + e.message);
    }
  };

  // ========== IMAGE SCHEMA FUNCTIONS ==========

  const fetchImageSchemas = async () => {
    if (!selectedTower || !selectedCommodity) return;

    try {
      setLoading(true);
      const response = await apiFetch(
        `${API_BASE}/image-schemas?tower=${selectedTower}&commodity=${selectedCommodity}`
      );
      const data = await response.json();
      setImageSchemas(data.versions || []);
    } catch (error) {
      setError("Failed to load image schemas");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadImageSchema = async (versionId) => {
    try {
      setLoading(true);
      const response = await apiFetch(`${API_BASE}/image-schemas/${versionId}`);
      const data = await response.json();
      setSelectedImageSchema(data);
    } catch (error) {
      setError("Failed to load image schema");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveImageSchema = async (schemaData) => {
    try {
      setSaving(true);
      const response = await apiFetch(`${API_BASE}/image-schemas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tower: selectedTower,
          commodity: selectedCommodity,
          ...schemaData,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Image schema version ${data.version} created successfully!`);
        setImageSchemaDialogOpen(false);
        fetchImageSchemas();
      } else {
        setError(data.error || "Failed to save image schema");
      }
    } catch (error) {
      setError("Failed to save image schema: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleActivateImageSchema = async (versionId) => {
    try {
      setSaving(true);
      const response = await apiFetch(`${API_BASE}/image-schemas/${versionId}/activate`, {
        method: "POST",
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess("Image schema activated successfully!");
        fetchImageSchemas();
      } else {
        setError(data.error || "Failed to activate image schema");
      }
    } catch (error) {
      setError("Failed to activate image schema");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteImageSchema = async (versionId) => {
    if (!window.confirm("Are you sure you want to delete this image schema version?")) {
      return;
    }

    try {
      setSaving(true);
      const response = await apiFetch(`${API_BASE}/image-schemas/${versionId}`, {
        method: "DELETE",
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess("Image schema deleted successfully!");
        fetchImageSchemas();
      } else {
        setError(data.error || "Failed to delete image schema");
      }
    } catch (error) {
      setError("Failed to delete image schema");
    } finally {
      setSaving(false);
    }
  };

  // Flatten JSON for table view
  const flattenJSON = (obj, prefix = "", result = []) => {
    for (const key in obj) {
      const value = obj[key];
      const path = prefix ? `${prefix}.${key}` : key;

      if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        flattenJSON(value, path, result);
      } else {
        result.push({
          path: path,
          key: key,
          value: value,
          type: Array.isArray(value) ? "array" : typeof value,
        });
      }
    }
    return result;
  };

  // Reconstruct JSON from flat table data
  const reconstructJSON = (flatData) => {
    const result = {};

    flatData.forEach((item) => {
      const keys = item.path.split(".");
      let current = result;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = item.value;
    });

    return result;
  };

  // Update value in nested object by path
  const updateValueByPath = (obj, path, value) => {
    const keys = path.split(".").slice(1); // Remove 'root' prefix
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }

    const lastKey = keys[keys.length - 1];

    // Try to parse as JSON for objects/arrays/numbers/booleans
    try {
      const parsedValue = JSON.parse(value);
      current[lastKey] = parsedValue;
    } catch (e) {
      // If parsing fails, treat as string
      current[lastKey] = value;
    }

    return obj;
  };

  const handleTreeEdit = (path, currentValue) => {
    setEditingNode(path);
    setEditValue(
      typeof currentValue === "object" ? JSON.stringify(currentValue) : String(currentValue)
    );
  };

  const handleTreeSave = (path) => {
    try {
      const updatedData = JSON.parse(JSON.stringify(treeData)); // Deep clone
      updateValueByPath(updatedData, path, editValue);
      setTreeData(updatedData);
      setEditorContent(JSON.stringify(updatedData, null, 2));
      setTableData(flattenJSON(updatedData));
      setEditingNode(null);
      setHasUnsavedChanges(true);
      setSuccess("Value updated. Click 'Save Changes' to persist to database.");
    } catch (e) {
      setError("Failed to update value: " + e.message);
    }
  };

  const handleTreeCancel = () => {
    setEditingNode(null);
    setEditValue("");
  };

  // Handle table cell edit
  const handleTableEdit = (path, currentValue) => {
    setEditingNode(path);
    setEditValue(
      typeof currentValue === "object" ? JSON.stringify(currentValue) : String(currentValue)
    );
  };

  const handleTableSave = (path) => {
    try {
      const updatedTableData = tableData.map((item) => {
        if (item.path === path) {
          // Try to parse as JSON for proper type
          try {
            const parsedValue = JSON.parse(editValue);
            return { ...item, value: parsedValue };
          } catch (e) {
            return { ...item, value: editValue };
          }
        }
        return item;
      });

      setTableData(updatedTableData);
      const reconstructed = reconstructJSON(updatedTableData);
      setTreeData(reconstructed);
      setEditorContent(JSON.stringify(reconstructed, null, 2));
      setEditingNode(null);
      setHasUnsavedChanges(true);
      setSuccess("Value updated. Click 'Save Changes' to persist to database.");
    } catch (e) {
      setError("Failed to update value: " + e.message);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);

      // Reconstruct the full schema from tabs
      const parsed = selectedSchema?.content_json || {
        operations: { unified_quick_cost: {} },
      };

      // Update only the unified_quick_cost section with tab values
      if (!parsed.operations) parsed.operations = {};
      if (!parsed.operations.unified_quick_cost) parsed.operations.unified_quick_cost = {};

      parsed.operations.unified_quick_cost.system_prompt = systemPrompt;
      parsed.operations.unified_quick_cost.user_prompt_template = userPromptTemplate;

      // Parse and validate output schema structure
      const schemaObj = JSON.parse(outputSchema);
      // Ensure proper JSON Schema structure with type: "object" at root
      if (schemaObj.properties && !schemaObj.type) {
        schemaObj.type = "object";
      }
      parsed.operations.unified_quick_cost.output_schema = schemaObj;

      parsed.operations.unified_quick_cost.model = modelConfig.model;
      parsed.operations.unified_quick_cost.temperature = modelConfig.temperature;
      parsed.operations.unified_quick_cost.max_tokens = modelConfig.max_tokens;

      // If creating new schema, use POST instead of PUT
      if (isCreatingNew) {
        // Ensure schema has required fields
        if (!parsed.schema_version) {
          parsed.schema_version = "1.0";
        }

        const payload = {
          tower: selectedTower,
          commodity: selectedCommodity,
          schema_name: `${selectedCommodity}_quick_cost`,
          version: newSchemaVersion,
          content_json: parsed,
          description: `New schema for ${selectedCommodity}`,
        };

        console.log("Creating new schema with payload:", payload);

        const response = await apiFetch(`${API_BASE}/schemas`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        console.log("Create schema response:", { status: response.status, data });

        if (response.ok) {
          setSuccess(`Schema ${newSchemaVersion} created successfully!`);
          setIsCreatingNew(false);
          setHasUnsavedChanges(false);
          fetchSchemas(); // Reload to show new schema
        } else {
          console.error("Failed to create schema:", data);
          setError(data.error || "Failed to create schema");
        }
        return;
      }

      // Save existing schema
      const response = await apiFetch(`${API_BASE}/schemas/${selectedSchema.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          version: selectedSchema.version,
          content_json: parsed,
          description: `Updated schema via editor`,
          overwrite: true,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess("Changes saved successfully!");
        setHasUnsavedChanges(false);
        // Don't reload schemas to preserve field order
        // fetchSchemas();
      } else {
        setError(data.error || "Failed to save changes");
      }
    } catch (error) {
      setError("Failed to save changes: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Validate Output Schema (JSON Schema format)
  const validateOutputSchema = async () => {
    try {
      setLoading(true);

      // Clear previous validation state
      setSchemaValidation({
        isValid: true,
        errors: [],
        warnings: [],
      });

      const schemaJson = JSON.parse(outputSchema);

      const response = await apiFetch(`${API_BASE}/validate-schema`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ schema: schemaJson }),
      });

      const data = await response.json();

      console.log("Validation response:", { status: response.status, data });

      // Handle both error responses (500) and error data in successful responses
      if (response.status === 500) {
        setSchemaValidation({
          isValid: false,
          errors: data.errors || [data.error || "Server error during validation"],
          warnings: data.warnings || [],
        });
        setError("Server validation error - check console for details");
        return;
      }

      setSchemaValidation({
        isValid: data.is_valid || false,
        errors: data.errors || [],
        warnings: data.warnings || [],
      });

      if (data.is_valid) {
        setSuccess("✓ Schema is valid!");
      } else {
        setError("Schema validation failed - see errors below");
      }
    } catch (error) {
      console.error("Validation error:", error);
      setSchemaValidation({
        isValid: false,
        errors: [error.message],
        warnings: [],
      });
      setError("Schema validation error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle tab content changes
  const handleSystemPromptChange = (value) => {
    setSystemPrompt(value || "");
    setHasUnsavedChanges(true);
  };

  const handleUserPromptChange = (value) => {
    setUserPromptTemplate(value || "");
    setHasUnsavedChanges(true);
  };

  const handleOutputSchemaChange = (value) => {
    setOutputSchema(value || "");
    setHasUnsavedChanges(true);
  };

  const handleModelConfigChange = (field, value) => {
    setModelConfig((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  // Clear schema cache (force reload from database)
  const handleClearCache = async () => {
    try {
      setLoading(true);
      const payload = {};

      // Include tower/commodity if selected
      if (selectedTower) {
        payload.tower = selectedTower;
        if (selectedCommodity) {
          payload.commodity = selectedCommodity;
        }
      }

      const response = await apiFetch(`${API_BASE}/clear-cache`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message || "Cache cleared successfully!");
      } else {
        setError(data.error || "Failed to clear cache");
      }
    } catch (error) {
      setError("Failed to clear cache: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const openSaveDialog = () => {
    // Auto-increment version suggestion
    if (selectedSchema) {
      const currentVersion = selectedSchema.version;
      const versionParts = currentVersion.split(".");
      versionParts[versionParts.length - 1] = String(
        parseInt(versionParts[versionParts.length - 1]) + 1
      );
      setNewVersion(versionParts.join("."));
    }
    setSaveDialogOpen(true);
  };

  // Render JSON as tree view with edit capability
  const renderTree = (data, path = "root") => {
    if (typeof data !== "object" || data === null) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            ml: 1,
            cursor: "pointer",
            p: 0.5,
            borderRadius: 1,
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.05)" },
          }}
          onClick={() => {
            setSelectedItem({ path, value: data, type: typeof data });
          }}
        >
          <Typography variant="body2" sx={{ color: "#ce9178", fontFamily: "monospace" }}>
            {typeof data === "string" && data.length > 100
              ? data.substring(0, 100) + "..."
              : JSON.stringify(data)}
          </Typography>
        </Box>
      );
    }

    return Object.keys(data).map((key) => {
      const value = data[key];
      const nodeId = `${path}.${key}`;
      const isObject = typeof value === "object" && value !== null;
      const isArray = Array.isArray(value);
      const depth = path.split(".").filter(Boolean).length;

      return (
        <TreeItem
          key={nodeId}
          itemId={nodeId}
          sx={{
            "& .MuiTreeItem-content": {
              paddingLeft: `${depth * 24}px`,
              borderLeft: depth > 0 ? "2px solid #404040" : "none",
              marginLeft: depth > 0 ? "16px" : "0",
              position: "relative",
              "&::before":
                depth > 0
                  ? {
                      content: '""',
                      position: "absolute",
                      left: "-16px",
                      top: "50%",
                      width: "14px",
                      height: "1px",
                      backgroundColor: "#404040",
                    }
                  : {},
            },
            "& .MuiTreeItem-group": {
              marginLeft: "0",
              paddingLeft: "0",
            },
          }}
          label={
            <Box sx={{ display: "flex", alignItems: "center", py: 0.5, gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#9cdcfe",
                  minWidth: "fit-content",
                  cursor: !isObject && !isArray ? "pointer" : "default",
                  "&:hover": !isObject && !isArray ? { color: "#4fc3f7" } : {},
                }}
                onClick={(e) => {
                  if (!isObject && !isArray) {
                    e.stopPropagation();
                    setSelectedItem({ path: nodeId, value, type: typeof value });
                  }
                }}
              >
                {key}:
              </Typography>
              {!isObject && !isArray && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    cursor: "pointer",
                    p: 0.5,
                    ml: 0.5,
                    borderRadius: 1,
                    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedItem({ path: nodeId, value, type: typeof value });
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#ce9178", fontFamily: "monospace", flex: 1 }}
                  >
                    {typeof value === "string" && value.length > 100
                      ? value.substring(0, 100) + "..."
                      : JSON.stringify(value)}
                  </Typography>
                  <EditIcon sx={{ fontSize: 14, color: "#858585", ml: 1 }} />
                </Box>
              )}
              {isArray && (
                <Chip
                  label={`Array[${value.length}]`}
                  size="small"
                  sx={{
                    height: 20,
                    backgroundColor: "#3c3c3c",
                    color: "#d4d4d4",
                    fontSize: "0.75rem",
                  }}
                />
              )}
              {isObject && !isArray && (
                <Chip
                  label={`Object{${Object.keys(value).length}}`}
                  size="small"
                  sx={{
                    height: 20,
                    backgroundColor: "#3c3c3c",
                    color: "#d4d4d4",
                    fontSize: "0.75rem",
                  }}
                />
              )}
            </Box>
          }
        >
          {isObject && renderTree(value, nodeId)}
        </TreeItem>
      );
    });
  };

  if (!isSuperAdmin) {
    return (
      <SidebarLayout>
        <Box sx={{ px: 2, pt: 2 }}>
          <Alert severity="warning">
            This page is available to Super Admin only.
          </Alert>
        </Box>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Compact Header */}
        {!focusMode && (
          <Box
            sx={{
              px: 2,
              py: 0.75,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            <AdminPanelSettingsIcon color="primary" sx={{ fontSize: 24 }} />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, fontSize: "1rem", lineHeight: 1.2 }}
              >
                Schema Management
              </Typography>
            </Box>
            <Chip label="Super Admin Only" color="error" size="small" />
          </Box>
        )}

        {/* Alerts */}
        {(error || success) && (
          <Box sx={{ px: 2, pt: 1, flexShrink: 0 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)} sx={{ py: 0.5 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" onClose={() => setSuccess(null)} sx={{ py: 0.5 }}>
                {success}
              </Alert>
            )}
          </Box>
        )}

        {/* Compact Controls */}
        {!focusMode && (
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexShrink: 0,
              backgroundColor: "background.paper",
            }}
          >
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Tower</InputLabel>
              <Select
                value={selectedTower}
                label="Tower"
                onChange={(e) => {
                  setSelectedTower(e.target.value);
                  setSelectedCommodity("");
                  setSelectedSchema(null);
                }}
              >
                {towers.map((tower) => (
                  <MenuItem key={tower.tower_code} value={tower.tower_code}>
                    {tower.tower_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }} disabled={!selectedTower}>
              <InputLabel>Commodity</InputLabel>
              <Select
                value={selectedCommodity}
                label="Commodity"
                onChange={(e) => {
                  setSelectedCommodity(e.target.value);
                  setSelectedSchema(null);
                }}
              >
                {commodities.map((commodity) => (
                  <MenuItem key={commodity.commodity_code} value={commodity.commodity_code}>
                    {commodity.commodity_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Active version badge next to Commodity */}
            {selectedSchema && activeSection === "data" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {isCreatingNew ? (
                  <Chip
                    label="New Schema"
                    color="info"
                    size="small"
                    icon={<AddCircleOutlineIcon />}
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      Active:
                    </Typography>
                    <Chip
                      label={selectedSchema.version}
                      color={selectedSchema.is_active ? "success" : "default"}
                      size="small"
                      icon={selectedSchema.is_active ? <CheckCircleIcon /> : null}
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  </>
                )}
              </Box>
            )}

            {/* Action buttons on the right */}
            {activeSection === "data" && (
              <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
                <Tooltip title="Refresh Schemas">
                  <span>
                    <IconButton
                      size="small"
                      onClick={fetchSchemas}
                      disabled={!selectedTower || !selectedCommodity}
                      sx={{
                        "&:disabled": {
                          bgcolor: "action.disabledBackground",
                          color: "action.disabled",
                        },
                      }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Enter Focus Mode">
                  <IconButton size="small" onClick={() => setFocusMode(true)}>
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Save button */}
                {selectedSchema && (
                  <Tooltip title={saving ? "Saving..." : "Save All Changes"}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleSaveChanges}
                        disabled={saving || !hasUnsavedChanges}
                        sx={{
                          color: hasUnsavedChanges ? "primary.main" : "action.disabled",
                          "&:disabled": { color: "action.disabled" },
                        }}
                      >
                        <SaveIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}

                {/* Clear Cache */}
                {selectedSchema && (
                  <Tooltip title="Clear Cache (Force Reload from DB)">
                    <IconButton size="small" onClick={handleClearCache} disabled={loading}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {/* New Version */}
                {selectedSchema && (
                  <Tooltip title="Save as New Version">
                    <span>
                      <IconButton size="small" onClick={openSaveDialog} disabled={!editorContent}>
                        <AddCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}

                {/* Create New Schema Button */}
                {selectedTower && selectedCommodity && !selectedSchema && schemas.length > 0 && (
                  <Tooltip title="Create New Schema Version">
                    <IconButton size="small" onClick={handleCreateNewSchema}>
                      <AddCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Navigation and Content Area */}
        <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
          {/* Left Navigation Panel */}
          <Box
            sx={{
              width: 60,
              backgroundColor: "background.paper",
              borderRight: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 2,
              gap: 1,
            }}
          >
            <Tooltip title="Data Schemas" placement="right">
              <IconButton
                onClick={() => {
                  setSectionLoading(true);
                  setActiveSection("data");
                  setTimeout(() => setSectionLoading(false), 300);
                }}
                sx={{
                  borderRadius: 1,
                  width: 48,
                  height: 48,
                  backgroundColor: activeSection === "data" ? "primary.main" : "transparent",
                  color: activeSection === "data" ? "white" : "text.secondary",
                  "&:hover": {
                    backgroundColor: activeSection === "data" ? "primary.dark" : "action.hover",
                  },
                }}
              >
                <DataObjectIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Image Schemas" placement="right">
              <IconButton
                onClick={() => {
                  setSectionLoading(true);
                  setActiveSection("image");
                  setTimeout(() => setSectionLoading(false), 300);
                }}
                sx={{
                  borderRadius: 1,
                  width: 48,
                  height: 48,
                  backgroundColor: activeSection === "image" ? "primary.main" : "transparent",
                  color: activeSection === "image" ? "white" : "text.secondary",
                  "&:hover": {
                    backgroundColor: activeSection === "image" ? "primary.dark" : "action.hover",
                  },
                }}
              >
                <PhotoLibraryIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Main Content Area */}
          {activeSection === "image" ? (
            <Box
              sx={{
                flex: 1,
                overflow: "hidden",
                width: "100%",
                maxWidth: "100%",
                display: "flex",
                position: "relative",
              }}
            >
              {sectionLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 1000,
                  }}
                >
                  <CircularProgress size={60} />
                </Box>
              )}
              <ImageSchemaManagementV2
                selectedTower={selectedTower}
                selectedCommodity={selectedCommodity}
              />
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              {sectionLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 1000,
                  }}
                >
                  <CircularProgress size={60} />
                </Box>
              )}
              {/* No Schema Selected - Show Create Option */}
              {!selectedSchema && selectedTower && selectedCommodity && schemas.length === 0 && (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 3,
                    p: 4,
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <DescriptionIcon sx={{ fontSize: 80, color: "text.disabled", mb: 2 }} />
                    <Typography variant="h5" gutterBottom color="text.secondary">
                      No Schemas Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      No schemas exist for {selectedTower} / {selectedCommodity}
                    </Typography>
                    <PerlButton
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={handleCreateNewSchema}
                      size="large"
                    >
                      Create New Schema
                    </PerlButton>
                  </Box>
                </Box>
              )}

              {/* Main Content - Full Height Editor */}
              {selectedSchema && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 0,
                    flex: 1,
                    overflow: "hidden",
                    width: "100%",
                    maxWidth: "100%",
                  }}
                >
                  {/* Editor Section - Full Height - Fixed Width */}
                  <Box
                    sx={{
                      flex: focusMode ? "1" : "0 0 70%",
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      borderRight: focusMode ? 0 : 1,
                      borderColor: "divider",
                      overflow: "hidden",
                    }}
                  >
                    {/* Editor Header with Tabs */}
                    <Box
                      sx={{
                        borderBottom: 1,
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        flexShrink: 0,
                      }}
                    >
                      {focusMode && (
                        <Box sx={{ px: 1, py: 0.5, display: "flex", justifyContent: "flex-end" }}>
                          <Tooltip title="Exit Focus Mode">
                            <IconButton size="small" onClick={() => setFocusMode(false)}>
                              <FullscreenExitIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}

                      {/* Tabs for Model Config, System Prompt, User Prompt, Schema */}
                      <Tabs
                        value={editorTab}
                        onChange={(e, v) => setEditorTab(v)}
                        sx={{ px: 2 }}
                        variant="standard"
                      >
                        <Tab label="Model Config" />
                        <Tab label="System Prompt" />
                        <Tab label="User Prompt" />
                        <Tab label="Output Schema" />
                      </Tabs>
                    </Box>

                    {/* Tabbed Editor Content */}
                    <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
                      {/* Tab 0: Model Configuration */}
                      {editorTab === 0 && (
                        <Box sx={{ height: "100%", overflow: "auto", p: 3 }}>
                          <Paper sx={{ p: 3, maxWidth: 600 }}>
                            <Typography variant="h6" gutterBottom>
                              AI Model Configuration
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              Configure the AI model settings for this schema
                            </Typography>

                            <FormControl fullWidth sx={{ mb: 3 }}>
                              <InputLabel>Model</InputLabel>
                              <Select
                                value={modelConfig.model}
                                label="Model"
                                onChange={(e) => handleModelConfigChange("model", e.target.value)}
                              >
                                <MenuItem value="gpt-5.1">GPT-5.1</MenuItem>
                                <MenuItem value="gpt-4o">GPT-4 Optimized (gpt-4o)</MenuItem>
                                <MenuItem value="gpt-4o-mini">GPT-4 Mini (gpt-4o-mini)</MenuItem>
                                <MenuItem value="gpt-4-turbo">GPT-4 Turbo (gpt-4-turbo)</MenuItem>
                                <MenuItem value="gpt-4">GPT-4 (gpt-4)</MenuItem>
                                <MenuItem value="gpt-3.5-turbo">
                                  GPT-3.5 Turbo (gpt-3.5-turbo)
                                </MenuItem>
                              </Select>
                            </FormControl>

                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Temperature: {modelConfig.temperature}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                gutterBottom
                              >
                                Controls randomness. Lower = more focused, Higher = more creative
                              </Typography>
                              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                <Typography variant="caption">0.0</Typography>
                                <Box sx={{ flex: 1 }}>
                                  <input
                                    type="range"
                                    min="0"
                                    max="2"
                                    step="0.1"
                                    value={modelConfig.temperature}
                                    onChange={(e) =>
                                      handleModelConfigChange(
                                        "temperature",
                                        parseFloat(e.target.value)
                                      )
                                    }
                                    style={{ width: "100%" }}
                                  />
                                </Box>
                                <Typography variant="caption">2.0</Typography>
                              </Box>
                            </Box>

                            <TextField
                              fullWidth
                              label="Max Tokens"
                              type="number"
                              value={modelConfig.max_tokens}
                              onChange={(e) =>
                                handleModelConfigChange("max_tokens", parseInt(e.target.value))
                              }
                              helperText="Maximum number of tokens in the response (1-8192)"
                              inputProps={{ min: 1, max: 8192 }}
                            />
                          </Paper>
                        </Box>
                      )}

                      {/* Tab 1: System Prompt */}
                      {editorTab === 1 && (
                        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                          <Box sx={{ px: 2, py: 1, backgroundColor: "background.default" }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              System Prompt
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Defines the AI agent's role, behavior, and context
                            </Typography>
                          </Box>
                          <Editor
                            height="calc(100% - 56px)"
                            defaultLanguage="markdown"
                            value={systemPrompt}
                            onChange={handleSystemPromptChange}
                            theme="vs-dark"
                            options={{
                              minimap: { enabled: false },
                              fontSize: 13,
                              wordWrap: "on",
                              lineNumbers: "on",
                              scrollBeyondLastLine: false,
                              automaticLayout: true,
                            }}
                          />
                        </Box>
                      )}

                      {/* Tab 2: User Prompt Template */}
                      {editorTab === 2 && (
                        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                          <Box sx={{ px: 2, py: 1, backgroundColor: "background.default" }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              User Prompt Template
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Template for user message with placeholders (e.g., {`{product_name}`})
                            </Typography>
                          </Box>
                          <Editor
                            height="calc(100% - 56px)"
                            defaultLanguage="markdown"
                            value={userPromptTemplate}
                            onChange={handleUserPromptChange}
                            theme="vs-dark"
                            options={{
                              minimap: { enabled: false },
                              fontSize: 13,
                              wordWrap: "off",
                              lineNumbers: "on",
                              scrollBeyondLastLine: false,
                              automaticLayout: true,
                              scrollbar: {
                                horizontal: "visible",
                                vertical: "visible",
                                horizontalScrollbarSize: 10,
                                verticalScrollbarSize: 10,
                              },
                            }}
                          />
                        </Box>
                      )}

                      {/* Tab 3: Output Schema (JSON Schema) */}
                      {editorTab === 3 && (
                        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                          <Box
                            sx={{
                              px: 2,
                              py: 1,
                              backgroundColor: "background.default",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Output Schema
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Defines the structure and validation rules for AI responses - saves
                                as JSON Schema
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Tooltip title="Schema Builder">
                                <IconButton
                                  size="small"
                                  onClick={() => setSchemaViewMode("builder")}
                                  sx={{
                                    color:
                                      schemaViewMode === "builder" ? "white" : "text.secondary",
                                  }}
                                >
                                  <ViewCompactIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="JSON Code">
                                <IconButton
                                  size="small"
                                  onClick={() => setSchemaViewMode("json")}
                                  sx={{
                                    color: schemaViewMode === "json" ? "white" : "text.secondary",
                                  }}
                                >
                                  <CodeIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title={
                                  schemaValidation.isValid ? "Schema is valid ✓" : "Validate Schema"
                                }
                              >
                                <IconButton
                                  size="small"
                                  onClick={validateOutputSchema}
                                  disabled={loading}
                                  sx={{
                                    color: schemaValidation.isValid ? "white" : "text.secondary",
                                    "&.Mui-disabled": {
                                      color: "action.disabled",
                                    },
                                  }}
                                >
                                  {loading ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <CheckCircleIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Add Field">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setNewField({
                                      name: "",
                                      type: "string",
                                      description: "",
                                      required: false,
                                      enum: [],
                                    });
                                    setEditingField(null);
                                    setFieldDialogOpen(true);
                                  }}
                                  sx={{
                                    color: "white",
                                  }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>

                          {/* Validation Status */}
                          {schemaValidation.errors.length > 0 && (
                            <Alert severity="error" sx={{ mx: 2, my: 1 }}>
                              <Typography variant="body2" fontWeight="bold">
                                Schema Errors:
                              </Typography>
                              {schemaValidation.errors.map((err, i) => (
                                <Typography key={i} variant="caption" display="block">
                                  • {err}
                                </Typography>
                              ))}
                            </Alert>
                          )}
                          {schemaValidation.warnings.length > 0 && (
                            <Alert severity="warning" sx={{ mx: 2, my: 1 }}>
                              <Typography variant="body2" fontWeight="bold">
                                Warnings:
                              </Typography>
                              {schemaValidation.warnings.map((warn, i) => (
                                <Typography key={i} variant="caption" display="block">
                                  • {warn}
                                </Typography>
                              ))}
                            </Alert>
                          )}

                          {/* Schema Builder Mode - User Friendly Form-Based Editor */}
                          {schemaViewMode === "builder" && (
                            <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                              <Box sx={{ maxWidth: 1400 }}>
                                {!outputSchema || outputSchema.trim() === "" ? (
                                  <Alert severity="info">
                                    No schema defined. Click "Add Field" to start building your
                                    schema.
                                  </Alert>
                                ) : (
                                  (() => {
                                    try {
                                      const schema = JSON.parse(outputSchema);
                                      const properties = schema.properties || {};
                                      const required = schema.required || [];

                                      // Recursive component to render fields
                                      const renderField = (
                                        key,
                                        value,
                                        path = "",
                                        depth = 0,
                                        parentRequired = [],
                                        index = 0,
                                        lastIndex = 0
                                      ) => {
                                        const fullPath = path ? `${path}.${key}` : key;
                                        const isExpanded = expandedFields[fullPath];
                                        const isRequired = parentRequired.includes(key);
                                        const hasNested =
                                          (value.type === "object" && value.properties) ||
                                          (value.type === "array" &&
                                            value.items &&
                                            value.items.properties);

                                        return (
                                          <Box key={fullPath}>
                                            <Paper
                                              elevation={2}
                                              onClick={() => setSelectedField(fullPath)}
                                              sx={{
                                                p: 1,
                                                mb: 1,
                                                ml: depth * 3,
                                                minHeight: "48px",
                                                maxHeight: "48px",
                                                border: 2,
                                                borderColor:
                                                  selectedField === fullPath
                                                    ? "primary.dark"
                                                    : "divider",
                                                backgroundColor:
                                                  selectedField === fullPath
                                                    ? perlColors.black
                                                    : movingField === fullPath
                                                    ? "action.selected"
                                                    : "background.paper",
                                                "&:hover": {
                                                  boxShadow: 4,
                                                  cursor: "pointer",
                                                },
                                                transition:
                                                  "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                                                overflow: "hidden",
                                                transform:
                                                  movingField === fullPath
                                                    ? "scale(1.02)"
                                                    : "scale(1)",
                                                boxShadow:
                                                  movingField === fullPath
                                                    ? "0 4px 20px rgba(25, 118, 210, 0.4)"
                                                    : undefined,
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 2,
                                                  width: "100%",
                                                }}
                                              >
                                                {/* Expand/collapse for nested */}
                                                {hasNested ? (
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                      setExpandedFields((prev) => ({
                                                        ...prev,
                                                        [fullPath]: !prev[fullPath],
                                                      }));
                                                    }}
                                                  >
                                                    {isExpanded ? (
                                                      <ExpandMoreIcon />
                                                    ) : (
                                                      <ChevronRightIcon />
                                                    )}
                                                  </IconButton>
                                                ) : depth > 0 ? (
                                                  <Box sx={{ width: 40 }} />
                                                ) : null}

                                                {/* Field name and type in one line */}
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    flex: 1,
                                                    minWidth: 0,
                                                  }}
                                                >
                                                  <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                      fontWeight: 600,
                                                      color: "primary.main",
                                                      fontSize: "0.95rem",
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    {key}
                                                  </Typography>
                                                  <Chip
                                                    label={value.type}
                                                    size="small"
                                                    color={
                                                      value.type === "object"
                                                        ? "secondary"
                                                        : value.type === "array"
                                                        ? "info"
                                                        : value.type === "number"
                                                        ? "success"
                                                        : "default"
                                                    }
                                                  />
                                                  {isRequired && (
                                                    <Chip
                                                      label="Required"
                                                      size="small"
                                                      color="error"
                                                    />
                                                  )}
                                                  {value.enum && (
                                                    <Chip
                                                      label="Enum"
                                                      size="small"
                                                      color="warning"
                                                    />
                                                  )}
                                                  {value.minimum !== undefined && (
                                                    <Chip
                                                      label={`min: ${value.minimum}`}
                                                      size="small"
                                                      variant="outlined"
                                                    />
                                                  )}
                                                  {value.maximum !== undefined && (
                                                    <Chip
                                                      label={`max: ${value.maximum}`}
                                                      size="small"
                                                      variant="outlined"
                                                    />
                                                  )}
                                                  {value.minLength !== undefined && (
                                                    <Chip
                                                      label={`minLength: ${value.minLength}`}
                                                      size="small"
                                                      variant="outlined"
                                                    />
                                                  )}
                                                  {value.maxLength !== undefined && (
                                                    <Chip
                                                      label={`maxLength: ${value.maxLength}`}
                                                      size="small"
                                                      variant="outlined"
                                                    />
                                                  )}
                                                  {value.pattern && (
                                                    <Chip
                                                      label={`pattern: ${value.pattern}`}
                                                      size="small"
                                                      variant="outlined"
                                                    />
                                                  )}
                                                  {value.format && (
                                                    <Chip
                                                      label={`format: ${value.format}`}
                                                      size="small"
                                                      variant="outlined"
                                                      color="info"
                                                    />
                                                  )}
                                                  {value.enum && (
                                                    <Chip
                                                      label={`${value.enum.length} options`}
                                                      size="small"
                                                      variant="outlined"
                                                      color="warning"
                                                    />
                                                  )}
                                                  {value.type === "array" &&
                                                    value.items &&
                                                    !isExpanded && (
                                                      <Chip
                                                        label={`Array items: ${
                                                          value.items.type || "object"
                                                        }${
                                                          value.items.properties
                                                            ? ` (${
                                                                Object.keys(value.items.properties)
                                                                  .length
                                                              } fields)`
                                                            : ""
                                                        }`}
                                                        size="small"
                                                        variant="outlined"
                                                        color="info"
                                                      />
                                                    )}
                                                  {value.type === "object" &&
                                                    value.properties &&
                                                    !isExpanded && (
                                                      <Chip
                                                        label={`${
                                                          Object.keys(value.properties).length
                                                        } nested fields`}
                                                        size="small"
                                                        variant="outlined"
                                                        color="secondary"
                                                      />
                                                    )}
                                                </Box>

                                                <Box sx={{ display: "flex", gap: 1 }}>
                                                  {/* Up/Down arrows - only visible when selected */}
                                                  {selectedField === fullPath && (
                                                    <>
                                                      <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleMoveFieldNested(
                                                            path,
                                                            key,
                                                            "up",
                                                            depth
                                                          );
                                                        }}
                                                        disabled={index === 0}
                                                        sx={{ p: 0.5 }}
                                                      >
                                                        <ArrowUpwardIcon fontSize="small" />
                                                      </IconButton>
                                                      <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleMoveFieldNested(
                                                            path,
                                                            key,
                                                            "down",
                                                            depth
                                                          );
                                                        }}
                                                        disabled={index === lastIndex}
                                                        sx={{ p: 0.5 }}
                                                      >
                                                        <ArrowDownwardIcon fontSize="small" />
                                                      </IconButton>
                                                    </>
                                                  )}
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                      setEditingField(fullPath);
                                                      setNewField({
                                                        name: key,
                                                        type: value.type,
                                                        description: value.description || "",
                                                        required: isRequired,
                                                        enum: value.enum || [],
                                                        minimum: value.minimum,
                                                        maximum: value.maximum,
                                                        minLength: value.minLength,
                                                        maxLength: value.maxLength,
                                                        pattern: value.pattern || "",
                                                        format: value.format || "",
                                                      });
                                                      setFieldDialogOpen(true);
                                                    }}
                                                  >
                                                    <EditIcon />
                                                  </IconButton>
                                                  <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                      const newSchema = JSON.parse(
                                                        JSON.stringify(schema)
                                                      );

                                                      if (!path) {
                                                        // Top-level field
                                                        delete newSchema.properties[key];
                                                        if (newSchema.required) {
                                                          newSchema.required =
                                                            newSchema.required.filter(
                                                              (f) => f !== key
                                                            );
                                                        }
                                                      } else {
                                                        // Nested field - navigate to parent and delete
                                                        const pathParts = path.split(".");
                                                        let targetObj = newSchema.properties;
                                                        let parentObj = null;
                                                        let parentKey = null;

                                                        // Navigate to the parent object
                                                        for (let i = 0; i < pathParts.length; i++) {
                                                          const part = pathParts[i];

                                                          if (part === "items") {
                                                            // Array items path
                                                            if (
                                                              targetObj.items &&
                                                              targetObj.items.properties
                                                            ) {
                                                              parentObj = targetObj.items;
                                                              targetObj =
                                                                targetObj.items.properties;
                                                            }
                                                          } else if (targetObj[part]) {
                                                            if (
                                                              targetObj[part].type === "object" &&
                                                              targetObj[part].properties
                                                            ) {
                                                              parentObj = targetObj[part];
                                                              targetObj =
                                                                targetObj[part].properties;
                                                            } else if (
                                                              targetObj[part].type === "array" &&
                                                              targetObj[part].items &&
                                                              targetObj[part].items.properties
                                                            ) {
                                                              parentObj = targetObj[part];
                                                              targetObj =
                                                                targetObj[part].items.properties;
                                                            }
                                                          }
                                                        }

                                                        // Delete the field
                                                        delete targetObj[key];

                                                        // Remove from required array if present
                                                        if (parentObj && parentObj.required) {
                                                          parentObj.required =
                                                            parentObj.required.filter(
                                                              (f) => f !== key
                                                            );
                                                        }
                                                      }

                                                      setOutputSchema(
                                                        JSON.stringify(newSchema, null, 2)
                                                      );
                                                      setHasUnsavedChanges(true);
                                                      setSuccess(`Field "${key}" deleted`);
                                                    }}
                                                  >
                                                    <DeleteIcon />
                                                  </IconButton>
                                                </Box>
                                              </Box>
                                            </Paper>

                                            {/* Render nested fields when expanded */}
                                            {isExpanded && (
                                              <Box sx={{ ml: 1 }}>
                                                {value.type === "object" && value.properties && (
                                                  <Box>
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                      sx={{
                                                        ml: 3,
                                                        mb: 0.5,
                                                        display: "block",
                                                        fontWeight: 500,
                                                      }}
                                                    >
                                                      Object Properties:
                                                    </Typography>
                                                    {Object.entries(value.properties).map(
                                                      ([nestedKey, nestedValue], idx, arr) =>
                                                        renderField(
                                                          nestedKey,
                                                          nestedValue,
                                                          fullPath,
                                                          depth + 1,
                                                          value.required || [],
                                                          idx,
                                                          arr.length - 1
                                                        )
                                                    )}
                                                  </Box>
                                                )}
                                                {value.type === "array" &&
                                                  value.items &&
                                                  value.items.properties && (
                                                    <Box>
                                                      <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                          ml: 3,
                                                          mb: 0.5,
                                                          display: "block",
                                                          fontWeight: 500,
                                                        }}
                                                      >
                                                        Array Item Properties:
                                                      </Typography>
                                                      {Object.entries(value.items.properties).map(
                                                        ([nestedKey, nestedValue], idx, arr) =>
                                                          renderField(
                                                            nestedKey,
                                                            nestedValue,
                                                            `${fullPath}.items`,
                                                            depth + 1,
                                                            value.items.required || [],
                                                            idx,
                                                            arr.length - 1
                                                          )
                                                      )}
                                                    </Box>
                                                  )}
                                              </Box>
                                            )}
                                          </Box>
                                        );
                                      };

                                      const propertyKeys = Object.keys(properties);

                                      return (
                                        <Box>
                                          {propertyKeys.length === 0 ? (
                                            <Alert severity="info">
                                              No fields defined. Click "Add Field" to start building
                                              your schema.
                                            </Alert>
                                          ) : (
                                            propertyKeys.map((key, index) =>
                                              renderField(
                                                key,
                                                properties[key],
                                                "",
                                                0,
                                                required,
                                                index,
                                                propertyKeys.length - 1
                                              )
                                            )
                                          )}
                                        </Box>
                                      );
                                    } catch (e) {
                                      console.error("Schema parse error:", e);
                                      return (
                                        <Alert severity="error">
                                          Invalid JSON Schema. Switch to JSON Code view to fix
                                          syntax errors: {e.message}
                                        </Alert>
                                      );
                                    }
                                  })()
                                )}
                              </Box>
                            </Box>
                          )}

                          {/* Preview Mode - Read-only Visual View */}
                          {schemaViewMode === "visual" && (
                            <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
                              <Paper sx={{ p: 3, maxWidth: 1200 }}>
                                <Typography variant="h6" gutterBottom>
                                  Schema Structure
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                  This shows a simplified view of your schema. For complex nested
                                  structures, use JSON View.
                                </Typography>

                                <Alert severity="info" sx={{ mb: 3 }}>
                                  <Typography variant="body2">
                                    <strong>Note:</strong> Your schema has{" "}
                                    {outputSchema
                                      ? Object.keys(JSON.parse(outputSchema).properties || {})
                                          .length
                                      : 0}{" "}
                                    top-level fields. Complex nested objects are shown in JSON View
                                    for easier editing.
                                  </Typography>
                                </Alert>

                                {outputSchema &&
                                  (() => {
                                    try {
                                      const schema = JSON.parse(outputSchema);
                                      const properties = schema.properties || {};

                                      return Object.entries(properties).map(([key, value]) => (
                                        <Paper key={key} variant="outlined" sx={{ p: 2, mb: 2 }}>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "flex-start",
                                            }}
                                          >
                                            <Box sx={{ flex: 1 }}>
                                              <Typography
                                                variant="subtitle1"
                                                sx={{ fontWeight: 600, color: "primary.main" }}
                                              >
                                                {key}
                                              </Typography>
                                              <Chip
                                                label={value.type}
                                                size="small"
                                                sx={{ mt: 0.5, mr: 1 }}
                                                color={
                                                  value.type === "object"
                                                    ? "secondary"
                                                    : value.type === "array"
                                                    ? "info"
                                                    : "default"
                                                }
                                              />
                                              {schema.required?.includes(key) && (
                                                <Chip
                                                  label="Required"
                                                  size="small"
                                                  color="error"
                                                  sx={{ mt: 0.5 }}
                                                />
                                              )}
                                              {value.description && (
                                                <Typography
                                                  variant="body2"
                                                  color="text.secondary"
                                                  sx={{ mt: 1 }}
                                                >
                                                  {value.description}
                                                </Typography>
                                              )}

                                              {/* Show nested structure info */}
                                              {value.type === "object" && value.properties && (
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{ mt: 1, display: "block" }}
                                                >
                                                  Contains {Object.keys(value.properties).length}{" "}
                                                  nested fields:{" "}
                                                  {Object.keys(value.properties)
                                                    .slice(0, 5)
                                                    .join(", ")}
                                                  {Object.keys(value.properties).length > 5 &&
                                                    "..."}
                                                </Typography>
                                              )}
                                              {value.type === "array" && value.items && (
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{ mt: 1, display: "block" }}
                                                >
                                                  Array of: {value.items.type || "objects"}
                                                  {value.items.properties &&
                                                    ` (${
                                                      Object.keys(value.items.properties).length
                                                    } fields)`}
                                                </Typography>
                                              )}
                                            </Box>
                                          </Box>
                                        </Paper>
                                      ));
                                    } catch (e) {
                                      return (
                                        <Alert severity="error">
                                          Invalid JSON Schema. Switch to JSON View to fix syntax
                                          errors.
                                        </Alert>
                                      );
                                    }
                                  })()}

                                <Button
                                  variant="outlined"
                                  fullWidth
                                  sx={{ mt: 2 }}
                                  onClick={() => setSchemaViewMode("json")}
                                >
                                  Switch to JSON View for Full Editing
                                </Button>
                              </Paper>
                            </Box>
                          )}

                          {/* JSON Editor Mode */}
                          {schemaViewMode === "json" && (
                            <Editor
                              height="100%"
                              defaultLanguage="json"
                              value={outputSchema}
                              onChange={handleOutputSchemaChange}
                              theme="vs-dark"
                              options={{
                                minimap: { enabled: true },
                                fontSize: 13,
                                formatOnPaste: true,
                                formatOnType: true,
                                automaticLayout: true,
                                scrollBeyondLastLine: false,
                                wordWrap: "off",
                                folding: true,
                                lineNumbers: "on",
                                scrollbar: {
                                  horizontal: "visible",
                                  vertical: "visible",
                                  horizontalScrollbarSize: 10,
                                  verticalScrollbarSize: 10,
                                },
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Sidebar - Property Panel / Versions & History - Fixed Width */}
                  {!focusMode && (
                    <Box
                      sx={{
                        flex: "0 0 30%",
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        borderLeft: 1,
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        overflow: "hidden",
                      }}
                    >
                      <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{
                          borderBottom: 1,
                          borderColor: "divider",
                          flexShrink: 0,
                          minHeight: 42,
                        }}
                      >
                        <Tab label="Versions" sx={{ minHeight: 42 }} />
                        {/* <Tab label="Image Schemas" sx={{ minHeight: 42 }} /> */}
                        <Tab label="Audit Log" sx={{ minHeight: 42 }} />
                      </Tabs>

                      <Box sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
                        {activeTab === 0 && (
                          <List dense>
                            {versions.map((version) => {
                              const isLoaded = selectedSchema?.id === version.id;
                              return (
                                <ListItem
                                  key={version.id}
                                  onClick={() => loadSchema(version.id, true)}
                                  sx={{
                                    mb: 1,
                                    border: 2,
                                    borderColor: isLoaded ? "success.main" : "divider",
                                    borderRadius: 1,
                                    bgcolor: isLoaded
                                      ? "success.50"
                                      : version.is_active
                                      ? "action.hover"
                                      : "transparent",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                    "&:hover": {
                                      bgcolor: isLoaded ? "success.100" : "action.hover",
                                      borderColor: isLoaded ? "success.dark" : "primary.main",
                                      transform: "translateX(2px)",
                                    },
                                  }}
                                >
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography variant="body2" fontWeight="bold">
                                          v{version.version}
                                        </Typography>
                                        {version.is_active && (
                                          <Chip label="Active" size="small" color="success" />
                                        )}
                                        {isLoaded && (
                                          <Chip
                                            label="Loaded"
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                          />
                                        )}
                                      </Box>
                                    }
                                    secondary={
                                      <>
                                        <Typography variant="caption" display="block">
                                          {version.description || "No description"}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {new Date(version.created_at).toLocaleString()}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                        >
                                          by {version.created_by}
                                        </Typography>
                                      </>
                                    }
                                  />
                                  <ListItemSecondaryAction>
                                    {!version.is_active && (
                                      <Tooltip title="Activate this version">
                                        <IconButton
                                          edge="end"
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setVersionToActivate(version.id);
                                            setActivateDialogOpen(true);
                                          }}
                                          sx={{
                                            bgcolor: "success.50",
                                            "&:hover": { bgcolor: "success.100" },
                                          }}
                                        >
                                          <PlayArrowIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </ListItemSecondaryAction>
                                </ListItem>
                              );
                            })}
                          </List>
                        )}

                        {/* Image Schemas Tab */}
                        {activeTab === 1 && (
                          <Box>
                            <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
                              <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  setSelectedImageSchema(null);
                                  setImageSchemaDialogOpen(true);
                                }}
                                size="small"
                              >
                                Create New Version
                              </Button>
                            </Box>

                            {imageSchemas.length === 0 ? (
                              <Alert severity="info">
                                No image schemas found. Click "Create New Version" to get started.
                              </Alert>
                            ) : (
                              <List dense>
                                {imageSchemas.map((schema) => (
                                  <ListItem
                                    key={schema.id}
                                    sx={{
                                      mb: 1,
                                      border: 1,
                                      borderColor: schema.is_active ? "success.main" : "divider",
                                      borderRadius: 1,
                                      bgcolor: schema.is_active ? "success.50" : "transparent",
                                    }}
                                  >
                                    <ListItemText
                                      primary={
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                          <Typography variant="body2" fontWeight="bold">
                                            v{schema.version}
                                          </Typography>
                                          {schema.is_active && (
                                            <Chip label="Active" size="small" color="success" />
                                          )}
                                          <Chip
                                            label={schema.model}
                                            size="small"
                                            variant="outlined"
                                          />
                                          <Chip
                                            label={schema.size}
                                            size="small"
                                            variant="outlined"
                                          />
                                        </Box>
                                      }
                                      secondary={
                                        <>
                                          <Typography variant="caption" display="block">
                                            {schema.description || "No description"}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {new Date(schema.created_at).toLocaleString()} by{" "}
                                            {schema.created_by}
                                          </Typography>
                                        </>
                                      }
                                    />
                                    <ListItemSecondaryAction>
                                      <Box sx={{ display: "flex", gap: 0.5 }}>
                                        <Tooltip title="View/Edit">
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              loadImageSchema(schema.id);
                                              setImageSchemaDialogOpen(true);
                                            }}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        {!schema.is_active && (
                                          <>
                                            <Tooltip title="Activate">
                                              <IconButton
                                                size="small"
                                                onClick={() => handleActivateImageSchema(schema.id)}
                                              >
                                                <PlayArrowIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                              <IconButton
                                                size="small"
                                                onClick={() => handleDeleteImageSchema(schema.id)}
                                                color="error"
                                              >
                                                <DeleteIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </>
                                        )}
                                      </Box>
                                    </ListItemSecondaryAction>
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </Box>
                        )}

                        {/* Audit Log Tab */}
                        {activeTab === 2 && (
                          <List dense>
                            {auditLog.map((log) => (
                              <ListItem
                                key={log.id}
                                sx={{ mb: 1, flexDirection: "column", alignItems: "flex-start" }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  <Chip
                                    label={log.action}
                                    size="small"
                                    color={
                                      log.action === "ACTIVATE"
                                        ? "success"
                                        : log.action === "CREATE"
                                        ? "primary"
                                        : log.action === "DEACTIVATE"
                                        ? "warning"
                                        : "default"
                                    }
                                  />
                                  <Typography variant="caption" sx={{ ml: "auto" }}>
                                    {new Date(log.changed_at).toLocaleString()}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  v{log.version} by {log.changed_by}
                                </Typography>
                                {log.notes && (
                                  <Typography variant="caption" color="text.secondary">
                                    {log.notes}
                                  </Typography>
                                )}
                                <Divider sx={{ width: "100%", mt: 1 }} />
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* No Selection State */}
              {!selectedSchema && selectedTower && selectedCommodity && !loading && (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h6" color="text.secondary">
                      No schema found for {selectedTower} / {selectedCommodity}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Create a new schema to get started
                    </Typography>
                  </Box>
                </Box>
              )}

              {!selectedTower && (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h6" color="text.secondary">
                      Select a Tower and Commodity to begin
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Dialogs - outside the navigation/content area but inside main Box */}
        {/* Save Dialog */}
        <Dialog
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Save New Version</DialogTitle>
          <DialogContent>
            <TextField
              label="Version Number"
              value={newVersion}
              onChange={(e) => setNewVersion(e.target.value)}
              fullWidth
              sx={{ mt: 2, mb: 2 }}
              helperText="e.g., 1.1.0, 2.0.0"
            />
            <TextField
              label="Description"
              value={versionDescription}
              onChange={(e) => setVersionDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText="What changed in this version?"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveSchema}
              variant="contained"
              disabled={!newVersion || saving}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {saving ? "Saving..." : "Save Version"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Activate Dialog */}
        <Dialog open={activateDialogOpen} onClose={() => setActivateDialogOpen(false)}>
          <DialogTitle>Activate Version</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to activate this version? This will deactivate the current
              active version.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActivateDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleActivateVersion}
              variant="contained"
              color="success"
              disabled={saving}
            >
              {saving ? "Activating..." : "Activate"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Field Editor Dialog - User Friendly Form */}
        <Dialog
          open={fieldDialogOpen}
          onClose={() => setFieldDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingField ? `Edit Field: ${editingField}` : "Add New Field"}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
              <TextField
                label="Field Name"
                value={newField.name}
                onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                fullWidth
                disabled={editingField !== null}
                helperText={
                  editingField ? "Field name cannot be changed" : "Use snake_case or camelCase"
                }
              />

              <FormControl fullWidth>
                <InputLabel>Field Type</InputLabel>
                <Select
                  value={newField.type}
                  label="Field Type"
                  onChange={(e) => setNewField({ ...newField, type: e.target.value })}
                >
                  <MenuItem value="string">String (text)</MenuItem>
                  <MenuItem value="number">Number (integer or decimal)</MenuItem>
                  <MenuItem value="integer">Integer (whole numbers only)</MenuItem>
                  <MenuItem value="boolean">Boolean (true/false)</MenuItem>
                  <MenuItem value="array">Array (list of items)</MenuItem>
                  <MenuItem value="object">Object (nested structure)</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Description"
                value={newField.description}
                onChange={(e) => setNewField({ ...newField, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                helperText="Explain what this field is for"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={newField.required}
                    onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                  />
                }
                label="Required Field"
              />

              {/* Number Constraints */}
              {(newField.type === "number" || newField.type === "integer") && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="Minimum Value"
                    type="number"
                    value={newField.minimum ?? ""}
                    onChange={(e) =>
                      setNewField({
                        ...newField,
                        minimum: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                    fullWidth
                  />
                  <TextField
                    label="Maximum Value"
                    type="number"
                    value={newField.maximum ?? ""}
                    onChange={(e) =>
                      setNewField({
                        ...newField,
                        maximum: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                    fullWidth
                  />
                </Box>
              )}

              {/* String Constraints */}
              {newField.type === "string" && (
                <>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      label="Min Length"
                      type="number"
                      value={newField.minLength ?? ""}
                      onChange={(e) =>
                        setNewField({
                          ...newField,
                          minLength: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      fullWidth
                    />
                    <TextField
                      label="Max Length"
                      type="number"
                      value={newField.maxLength ?? ""}
                      onChange={(e) =>
                        setNewField({
                          ...newField,
                          maxLength: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      fullWidth
                    />
                  </Box>

                  <FormControl fullWidth>
                    <InputLabel>Format</InputLabel>
                    <Select
                      value={newField.format || ""}
                      label="Format"
                      onChange={(e) => setNewField({ ...newField, format: e.target.value })}
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="date">Date (YYYY-MM-DD)</MenuItem>
                      <MenuItem value="date-time">DateTime (ISO 8601)</MenuItem>
                      <MenuItem value="email">Email Address</MenuItem>
                      <MenuItem value="uri">URL/URI</MenuItem>
                      <MenuItem value="uuid">UUID</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="Enum Values (comma-separated)"
                    value={Array.isArray(newField.enum) ? newField.enum.join(", ") : ""}
                    onChange={(e) =>
                      setNewField({
                        ...newField,
                        enum: e.target.value ? e.target.value.split(",").map((v) => v.trim()) : [],
                      })
                    }
                    fullWidth
                    helperText="Leave empty for free text, or add comma-separated options for dropdown"
                    placeholder="e.g., Premium, Standard, Economy"
                  />
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFieldDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                try {
                  const schema = JSON.parse(outputSchema);
                  if (!schema.properties) schema.properties = {};
                  if (!schema.required) schema.required = [];

                  // Build field definition
                  const fieldDef = {
                    type: newField.type,
                    description: newField.description,
                  };

                  // Add constraints based on type
                  if (newField.type === "string") {
                    if (newField.minLength !== undefined) fieldDef.minLength = newField.minLength;
                    if (newField.maxLength !== undefined) fieldDef.maxLength = newField.maxLength;
                    if (newField.format) fieldDef.format = newField.format;
                    if (newField.enum && newField.enum.length > 0) fieldDef.enum = newField.enum;
                  } else if (newField.type === "number" || newField.type === "integer") {
                    if (newField.minimum !== undefined) fieldDef.minimum = newField.minimum;
                    if (newField.maximum !== undefined) fieldDef.maximum = newField.maximum;
                  } else if (newField.type === "array") {
                    fieldDef.items = { type: "string" }; // Default
                  } else if (newField.type === "object") {
                    fieldDef.properties = {};
                  }

                  // Add or update field
                  if (editingField && editingField !== newField.name) {
                    // Rename: delete old, add new
                    delete schema.properties[editingField];
                    schema.required = schema.required.filter((f) => f !== editingField);
                  }

                  schema.properties[newField.name] = fieldDef;

                  // Update required array
                  if (newField.required && !schema.required.includes(newField.name)) {
                    schema.required.push(newField.name);
                  } else if (!newField.required && schema.required.includes(newField.name)) {
                    schema.required = schema.required.filter((f) => f !== newField.name);
                  }

                  setOutputSchema(JSON.stringify(schema, null, 2));
                  setHasUnsavedChanges(true);
                  setFieldDialogOpen(false);
                  setSuccess(
                    editingField
                      ? `Field "${newField.name}" updated`
                      : `Field "${newField.name}" added`
                  );
                } catch (e) {
                  setError(`Failed to save field: ${e.message}`);
                }
              }}
              variant="contained"
              disabled={!newField.name || !newField.type}
            >
              {editingField ? "Update Field" : "Add Field"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Version Dialog for New Schema */}
        <Dialog
          open={versionDialogOpen}
          onClose={() => setVersionDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AddCircleOutlineIcon color="primary" />
              <Typography variant="h6">Create New Schema</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Creating a new schema for <strong>{selectedCommodity}</strong> in tower{" "}
              <strong>{selectedTower}</strong>
            </Typography>
            <TextField
              label="Schema Version"
              value={newSchemaVersion}
              onChange={(e) => setNewSchemaVersion(e.target.value)}
              fullWidth
              required
              helperText="e.g., v1.0, v2.1, etc."
              placeholder="v1.0"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVersionDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={confirmCreateNewSchema}
              disabled={!newSchemaVersion.trim()}
            >
              Create Schema
            </Button>
          </DialogActions>
        </Dialog>

        {/* Prompt Editor Dialog */}
        <Dialog
          open={promptDialogOpen}
          onClose={() => setPromptDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EditIcon />
              <Typography variant="h6">
                Edit {promptType === "system" ? "System Prompt" : "User Prompt Template"}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {promptPath}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={20}
              value={promptValue}
              onChange={(e) => setPromptValue(e.target.value)}
              variant="outlined"
              placeholder={`Enter ${
                promptType === "system" ? "system prompt" : "user prompt template"
              }...`}
              sx={{
                mt: 2,
                fontFamily: "monospace",
                "& .MuiInputBase-input": {
                  fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                  fontSize: "13px",
                  lineHeight: 1.6,
                },
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPromptDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={savePromptChanges}>
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Image Schema Editor Dialog */}
        <Dialog
          open={imageSchemaDialogOpen}
          onClose={() => setImageSchemaDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <ImageSchemaEditor
            schema={selectedImageSchema}
            onSave={handleSaveImageSchema}
            onCancel={() => setImageSchemaDialogOpen(false)}
          />
        </Dialog>
      </Box>
    </SidebarLayout>
  );
};

export default SchemaManagementPage;
